import * as React from 'react'
import { connect } from 'react-redux'
import * as ReactStrap from 'reactstrap'
import { getRecaptchaToken } from '../recaptcha'

interface SurveyResultsRedirectProps {
  communityIdentifier?: string;
  recaptchaKey?: string;
}

interface SurveyResultsRedirectState {
  status: 'loading' | 'verifying' | 'redirecting' | 'error';
  errorMessage?: string;
}

class SurveyResultsRedirect extends React.Component<SurveyResultsRedirectProps, SurveyResultsRedirectState> {
  constructor(props: SurveyResultsRedirectProps) {
    super(props);
    this.state = {
      status: 'loading'
    };
  }

  componentDidMount() {
    this.verifyAndRedirect();
  }

  verifyAndRedirect = async () => {
    try {
      // Extract community identifier from path or props
      const pathParts = window.location.pathname.split('/');
      const communityIdentifier = this.props.communityIdentifier || pathParts[pathParts.length - 1];
      
      // Validate that we have a community identifier
      if (!communityIdentifier) {
        this.setState({ 
          status: 'error', 
          errorMessage: 'Invalid survey link. Community identifier is missing.' 
        });
        return;
      }

      // Only show verifying status and get reCAPTCHA if recaptcha key exists
      if (this.props.recaptchaKey) {
        this.setState({ status: 'verifying' });
        
        // Get reCAPTCHA token
        const recaptchaToken = await getRecaptchaToken('surveyResults', this.props.recaptchaKey);
        
        if (!recaptchaToken) {
          this.setState({ 
            status: 'error', 
            errorMessage: 'Failed to verify reCAPTCHA. Please try again.' 
          });
          return;
        }

        // Add reCAPTCHA token to URL params
        const urlParams = new URLSearchParams(window.location.search);
        urlParams.set('recaptcha', recaptchaToken);
        
        // Build new URL with reCAPTCHA token
        const newUrl = `/survey_results_view/${communityIdentifier}?${urlParams.toString()}`;
        window.location.replace(newUrl);
      } else {
        // No reCAPTCHA needed - immediate redirect
        const urlParams = new URLSearchParams(window.location.search);
        const newUrl = `/survey_results_view/${communityIdentifier}?${urlParams.toString()}`;
        window.location.replace(newUrl);
      }

    } catch (error) {
      this.setState({ 
        status: 'error', 
        errorMessage: 'An error occurred during verification. Please try again.' 
      });
    }
  };

  render() {
    const { status, errorMessage } = this.state;

    const getStatusMessage = () => {
      switch (status) {
        case 'loading':
          return 'Loading...';
        case 'verifying':
          return 'Verifying access...';
        case 'redirecting':
          return 'Redirecting...';
        case 'error':
          return 'Verification Failed';
        default:
          return 'Loading...';
      }
    };

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f8f9fa'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          maxWidth: '400px'
        }}>
          {status === 'error' ? (
            <ReactStrap.Alert color="danger">
              <h5>{getStatusMessage()}</h5>
              <p>{errorMessage}</p>
              <ReactStrap.Button 
                color="primary" 
                onClick={() => window.location.reload()}
                style={{ marginTop: '1rem' }}
              >
                Try Again
              </ReactStrap.Button>
            </ReactStrap.Alert>
          ) : (
            <>
              <div style={{
                fontSize: '1.5rem',
                color: '#6c757d',
                marginBottom: '1rem'
              }}>
                {getStatusMessage()}
              </div>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #7FAE1B',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }}></div>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  recaptchaKey: state.config?.recaptcha_key
});

const SurveyResultsRedirectConnected = connect(mapStateToProps)(SurveyResultsRedirect);

export { SurveyResultsRedirectConnected as SurveyResultsRedirect };